import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Bell, Briefcase, Home, LayoutGrid, Moon, Search, Sun, UserRound, Wallet, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../store/auth";
import { useTheme } from "../store/theme";
import { api } from "../lib/api";
import { useQuery } from "@tanstack/react-query";
import { Logo } from "./Logo";

type Suggest = { markets: { slug: string; question: string }[]; categories: { slug: string; name: string }[] };

export function Shell() {
  const { user, refresh, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [mobileSearch, setMobileSearch] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    setMobileSearch(false);
    setQ("");
  }, [loc.pathname]);
  useEffect(() => {
    if (mobileSearch) searchRef.current?.focus();
  }, [mobileSearch]);
  const suggest = useQuery({
    queryKey: ["suggest", q],
    queryFn: () => api<Suggest>(`/markets/search/suggest?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });

  const searchResults = q.length >= 2 && suggest.data && (
    <div className="search-pop" role="listbox">
      {suggest.data.markets.map((m) => (
        <Link key={m.slug} to={`/markets/${m.slug}`} onClick={() => { setQ(""); setMobileSearch(false); }} className="search-hit">
          {m.question}
        </Link>
      ))}
      {!suggest.data.markets.length && <div className="search-empty">No markets match that search.</div>}
    </div>
  );

  return (
    <>
      <div className="chrome">
        <header className="topnav">
          <div className="topnav-inner">
            <Link to="/" className="sw-logo-link" aria-label="Softwave Markets home">
              <Logo markSize={32} />
            </Link>
            <nav className="desktop-only" aria-label="Primary">
              <NavLink to="/markets">Markets</NavLink>
              <NavLink to="/markets?sort=trending">Trending</NavLink>
              <NavLink to="/portfolio">Portfolio</NavLink>
              <NavLink to="/learn">Learn</NavLink>
            </nav>
            <div className="search desktop-only">
              <Search size={16} />
              <input
                placeholder="Search markets"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search markets"
              />
              {searchResults}
            </div>
            <div className="topnav-right">
              <button
                className="icon-btn mobile-only"
                type="button"
                aria-label={mobileSearch ? "Close search" : "Search markets"}
                onClick={() => setMobileSearch((v) => !v)}
              >
                {mobileSearch ? <X size={20} /> : <Search size={20} />}
              </button>
              <button
                className="btn btn-ghost theme-toggle"
                onClick={() => toggle()}
                aria-label={theme === "night" ? "Switch to day mode" : "Switch to night mode"}
                title={theme === "night" ? "Day mode" : "Night mode"}
                type="button"
              >
                {theme === "night" ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <div className="desktop-only topnav-account">
                {user ? (
                  <>
                    <Link to="/activity" className="icon-btn" aria-label="Notifications"><Bell size={18} /></Link>
                    <Link to="/wallet" className="btn btn-ghost btn-sm"><Wallet size={16} /> Wallet</Link>
                    <Link to="/account" className="nav-user">{user.username}</Link>
                    {(user.role === "ADMIN" || user.role === "SUPER_ADMIN") && <Link to="/admin">Admin</Link>}
                    <button className="btn btn-ghost btn-sm" onClick={() => void logout()}>Sign out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="btn btn-ghost btn-sm">Sign in</Link>
                    <Link to="/register" className="btn btn-primary btn-sm">My account</Link>
                  </>
                )}
              </div>
            </div>
          </div>
          {mobileSearch && (
            <div className="mobile-search">
              <Search size={16} />
              <input
                ref={searchRef}
                placeholder="Search markets"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search markets"
              />
              {searchResults}
            </div>
          )}
        </header>
      </div>
      <Outlet />
      <Footer />
      <nav className="mobile-nav" aria-label="Mobile">
        <NavLink to="/" end>
          <Home size={22} strokeWidth={2.2} />
          <span>Home</span>
        </NavLink>
        <NavLink to="/markets">
          <LayoutGrid size={22} strokeWidth={2.2} />
          <span>Markets</span>
        </NavLink>
        <NavLink to="/wallet">
          <Wallet size={22} strokeWidth={2.2} />
          <span>Wallet</span>
        </NavLink>
        <NavLink to="/portfolio">
          <Briefcase size={22} strokeWidth={2.2} />
          <span>Portfolio</span>
        </NavLink>
        <NavLink to={user ? "/account" : "/login"}>
          <UserRound size={22} strokeWidth={2.2} />
          <span>{user ? "Account" : "Sign in"}</span>
        </NavLink>
      </nav>
    </>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>
          <Logo markSize={40} />
          <p className="footer-blurb">A prediction-market exchange. Users trade with each other. Softwave matches, accounts and settles — and earns a transparent fee.</p>
        </div>
        <div>
          <h4>Product</h4>
          <Link to="/markets">Markets</Link>
          <Link to="/how-it-works">How it works</Link>
          <Link to="/learn">Learn</Link>
          <Link to="/fees">Fee schedule</Link>
        </div>
        <div>
          <h4>Trust</h4>
          <Link to="/trust">Trust center</Link>
          <Link to="/status">System status</Link>
          <Link to="/resolution-rules">Resolution rules</Link>
          <Link to="/responsible-trading">Responsible trading</Link>
        </div>
        <div>
          <h4>Legal</h4>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/risk">Risk disclosure</Link>
          <Link to="/disputes">Disputes</Link>
        </div>
      </div>
      <div className="legal">Softwave Markets does not take the opposite side of ordinary customer flow. Demo data is labeled. This software is not an offer of services in any jurisdiction.</div>
    </footer>
  );
}
